import React, { useEffect } from 'react'
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useLoadboardTheme } from './loadboardTheme'

const EQUIPMENT_LABEL = {
	van: 'Van',
	powerOnly: 'PO',
}

const SOURCE_LABEL = {
	TQL: 'TQL',
	JBHUNT: 'JB Hunt',
	RXO: 'RXO',
	LANDSTAR: 'Landstar',
}

const formatEquipment = raw => {
	if (!raw) return 'Any'
	return raw
		.split(',')
		.map(s => EQUIPMENT_LABEL[s.trim()] || s.trim())
		.filter(Boolean)
		.join(', ')
}

const formatLastSeen = h => {
	const n = Number(h)
	if (!Number.isFinite(n) || n <= 0) return 'Any'
	if (n < 1) return `${Math.round(n * 60)} min`
	if (n === 1) return '1 hr'
	return `${n} hrs`
}

const formatWeight = w => {
	const n = Number(w)
	if (!Number.isFinite(n) || n <= 0) return 'Any'
	if (n >= 1000) return `≤${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k lb`
	return `≤${n} lb`
}

const formatDistance = (min, max) => {
	const mn = Number(min)
	const mx = Number(max)
	const hasMin = Number.isFinite(mn) && mn > 0
	const hasMax = Number.isFinite(mx) && mx > 0
	if (!hasMin && !hasMax) return 'Any'
	if (hasMin && hasMax) return `${mn.toLocaleString()}–${mx.toLocaleString()} mi`
	if (hasMin) return `≥${mn.toLocaleString()} mi`
	return `≤${mx.toLocaleString()} mi`
}

const formatSource = src => {
	if (!src || src === 'all') return 'All'
	const parts = src
		.split(',')
		.map(s => s.trim())
		.filter(Boolean)
	if (parts.length === 0) return 'All'
	const label = p => SOURCE_LABEL[p] || p
	if (parts.length === 1) return label(parts[0])
	return `${label(parts[0])} +${parts.length - 1}`
}

const GRID_COLS =
	'grid grid-cols-[minmax(120px,1.3fr)_minmax(110px,1.1fr)_minmax(100px,1fr)_70px_80px_90px_minmax(100px,1fr)_140px] items-center gap-2'

function ColumnHeader({ children, isLight, className = '' }) {
	const cls = isLight ? 'text-gray-400' : 'text-gray-500'
	return (
		<div className={`text-[9px] font-semibold uppercase tracking-wider ${cls} ${className}`}>
			{children}
		</div>
	)
}

function SearchRow({ item, tokens, isLight, onApply, onRemove }) {
	const { filters } = item
	const radius = filters.originDh ? ` (${filters.originDh})` : ''
	const destination = filters.destination?.trim() || 'Anywhere'

	const cellCls = `text-[12px] font-medium truncate ${tokens.valueDim}`

	return (
		<div
			className={`group ${GRID_COLS} px-5 py-3.5 border-b transition-colors cursor-pointer ${
				isLight ? 'border-gray-100 hover:bg-gray-50' : 'border-white/[0.04] hover:bg-white/[0.03]'
			}`}
			onClick={() => onApply(filters)}
		>
			<div className={`text-[12px] font-semibold truncate ${tokens.valueStrong}`}>
				{filters.origin || '—'}
				{radius && <span className={`font-normal ${tokens.valueMuted}`}>{radius}</span>}
			</div>
			<div className={`text-[12px] font-medium truncate min-w-0 ${tokens.valueDim}`}>
				{destination}
			</div>
			<div className={cellCls}>{formatEquipment(filters.equipment)}</div>
			<div className={cellCls}>{formatLastSeen(filters.lastSeenHours)}</div>
			<div className={cellCls}>{formatSource(filters.source)}</div>
			<div className={cellCls}>{formatWeight(filters.maxWeight)}</div>
			<div className={cellCls}>{formatDistance(filters.minDistance, filters.maxDistance)}</div>
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={e => {
						e.stopPropagation()
						onApply(filters)
					}}
					className="text-[11px] font-semibold text-sky-500 hover:text-sky-600 whitespace-nowrap"
				>
					APPLY FILTERS
				</button>
				<button
					type="button"
					onClick={e => {
						e.stopPropagation()
						onRemove(item.id)
					}}
					className={`p-1 rounded-full transition-colors ${
						isLight ? 'text-gray-400 hover:text-rose-500' : 'text-gray-500 hover:text-rose-400'
					}`}
					aria-label="Remove search"
					title="Remove search"
				>
					<TrashIcon className="h-4 w-4" />
				</button>
			</div>
		</div>
	)
}

function SavedSearchesModal({ isOpen, onClose, saved, onApply, onRemoveSaved }) {
	const { theme, tokens } = useLoadboardTheme()
	const isLight = theme === 'light'

	useEffect(() => {
		if (!isOpen) return
		const onKey = e => {
			if (e.key === 'Escape') onClose()
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [isOpen, onClose])

	if (!isOpen) return null

	const handleApply = filters => {
		onApply(filters)
		onClose()
	}

	return (
		<div
			className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-20 px-4"
			onClick={onClose}
		>
			<div
				className={`w-full max-w-4xl rounded-lg overflow-hidden shadow-xl ${
					isLight ? 'bg-white' : 'bg-[#15202b] ring-1 ring-white/10'
				}`}
				onClick={e => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-label="Saved searches"
			>
				<div
					className={`flex items-center justify-between px-5 py-3 border-b ${
						isLight ? 'border-gray-200' : 'border-white/[0.06]'
					}`}
				>
					<h2 className={`text-sm font-semibold ${tokens.valueStrong}`}>
						Saved searches ({saved.length})
					</h2>
					<button
						type="button"
						onClick={onClose}
						className={`flex items-center justify-center h-7 w-7 rounded-full transition-colors ${
							isLight
								? 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
								: 'text-gray-400 hover:text-gray-100 hover:bg-white/[0.08]'
						}`}
						aria-label="Close"
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>

				<div className="min-h-[320px] max-h-[65vh] overflow-y-auto">
					{saved.length === 0 ? (
						<div className={`text-center py-20 text-sm ${tokens.valueMuted}`}>
							No saved searches yet. Save a search to see it here.
						</div>
					) : (
						<>
							<div className={`${GRID_COLS} px-5 pt-3 pb-2`}>
								<ColumnHeader isLight={isLight}>Origin</ColumnHeader>
								<ColumnHeader isLight={isLight}>Destination</ColumnHeader>
								<ColumnHeader isLight={isLight}>Equipment</ColumnHeader>
								<ColumnHeader isLight={isLight}>Last seen</ColumnHeader>
								<ColumnHeader isLight={isLight}>Source</ColumnHeader>
								<ColumnHeader isLight={isLight}>Max weight</ColumnHeader>
								<ColumnHeader isLight={isLight}>Distance</ColumnHeader>
								<ColumnHeader isLight={isLight}>Actions</ColumnHeader>
							</div>
							{saved.map(item => (
								<SearchRow
									key={item.id}
									item={item}
									tokens={tokens}
									isLight={isLight}
									onApply={handleApply}
									onRemove={onRemoveSaved}
								/>
							))}
						</>
					)}
				</div>
			</div>
		</div>
	)
}

export default SavedSearchesModal
